import React from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../../Helper/mediaUrl";
import { normalizeExternalUrl } from "../../Helper/urls";
import { formatProjectMoney } from "./ProjectFundingPanel";

/**
 * Milestone progress + active campaign + history for public Project pages.
 */
function ProjectMilestones({
  milestones = [],
  activeMilestone = null,
  milestoneHistory = null,
  projectType = "one_time",
  progressPercent = null,
  supportPath = "",
  donationUrl = "",
}) {
  const list = Array.isArray(milestones) ? milestones : [];
  const history = Array.isArray(milestoneHistory)
    ? milestoneHistory
    : list.filter((m) => m.status === "completed");
  const active =
    activeMilestone || list.find((m) => m.status === "current") || null;
  const isOngoing = projectType === "ongoing";

  if (!list.length && (progressPercent === null || progressPercent === undefined) && !active) {
    return (
      <section
        className="project-milestones project-milestones--empty"
        aria-labelledby="project-milestones-heading"
      >
        <h2 id="project-milestones-heading" className="project-section-heading">
          Progress
        </h2>
        <p className="project-milestones__empty-copy">
          {isOngoing
            ? "Milestone campaigns will appear here when the project publishes its first goal."
            : "Progress milestones will appear here as this project advances."}
        </p>
      </section>
    );
  }

  const externalDonate = donationUrl ? normalizeExternalUrl(donationUrl) : "";

  return (
    <section
      className="project-milestones"
      aria-labelledby="project-milestones-heading"
    >
      <h2 id="project-milestones-heading" className="project-section-heading">
        {isOngoing ? "Campaign" : "Progress"}
      </h2>

      {isOngoing && active ? (
        <div className="project-active-milestone">
          <p className="project-active-milestone__label">Active milestone</p>
          <h3 className="project-active-milestone__title">{active.title}</h3>
          {active.recipient_name ? (
            <p className="project-active-milestone__recipient">
              Recipient: {active.recipient_name}
            </p>
          ) : null}
          {active.description ? (
            <p className="project-active-milestone__desc">{active.description}</p>
          ) : null}
          <div className="project-active-milestone__stats">
            <div>
              <strong>
                {formatProjectMoney(active.amount_raised, active.currency)}
              </strong>
              <span>raised</span>
            </div>
            {active.goal_amount != null ? (
              <div>
                <strong>
                  {formatProjectMoney(active.goal_amount, active.currency)}
                </strong>
                <span>goal</span>
              </div>
            ) : null}
            {active.funding_percent != null ? (
              <div>
                <strong>{active.funding_percent}%</strong>
                <span>funded</span>
              </div>
            ) : null}
            {active.days_remaining != null ? (
              <div>
                <strong>{active.days_remaining}</strong>
                <span>
                  {active.days_remaining === 1 ? "day left" : "days left"}
                </span>
              </div>
            ) : null}
          </div>
          {active.funding_percent != null ? (
            <div
              className="project-funding__bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={active.funding_percent}
            >
              <span style={{ width: `${active.funding_percent}%` }} />
            </div>
          ) : null}
          {active.image_path ? (
            <div className="project-active-milestone__media">
              <img
                src={resolveMediaUrl(active.image_path)}
                alt=""
                onError={handleMediaImgError}
              />
            </div>
          ) : null}
          <div className="project-active-milestone__actions">
            {externalDonate ? (
              <a
                href={externalDonate}
                className="btn btn-danger btn-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Support this milestone
              </a>
            ) : supportPath ? (
              <Link to={supportPath} className="btn btn-danger btn-sm">
                Support this milestone
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {!isOngoing && progressPercent !== null && progressPercent !== undefined ? (
        <p className="project-milestones__overall">
          Overall progress: <strong>{Number(progressPercent)}%</strong>
        </p>
      ) : null}

      {list.length ? (
        <ol className="project-milestones__list" aria-label="Milestone timeline">
          {list.map((item) => (
            <li
              key={item.id || item.title}
              className={
                "project-milestones__item project-milestones__item--" +
                (item.status || "upcoming")
              }
            >
              <div className="project-milestones__marker" aria-hidden="true" />
              <div className="project-milestones__body">
                <p className="project-milestones__status">
                  {(item.status || "upcoming").replace(/^\w/, (c) =>
                    c.toUpperCase()
                  )}
                  {item.target_date ? ` · ${item.target_date}` : ""}
                </p>
                <h3 className="project-milestones__title">{item.title}</h3>
                {item.recipient_name ? (
                  <p className="project-milestones__recipient">
                    {item.recipient_name}
                  </p>
                ) : null}
                {item.description ? (
                  <p className="project-milestones__desc">{item.description}</p>
                ) : null}
                {item.goal_amount != null || item.amount_raised ? (
                  <p className="project-milestones__funding">
                    {formatProjectMoney(item.amount_raised || 0, item.currency)}
                    {item.goal_amount != null
                      ? ` of ${formatProjectMoney(item.goal_amount, item.currency)}`
                      : ""}
                    {item.funding_percent != null
                      ? ` · ${item.funding_percent}%`
                      : ""}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {isOngoing && history.length ? (
        <div className="project-milestone-history">
          <h3 className="project-milestone-history__heading">Impact history</h3>
          <ul className="project-milestone-history__list">
            {history.map((item) => (
              <li key={`hist-${item.id || item.title}`}>
                <strong>{item.title}</strong>
                {item.recipient_name ? (
                  <span> · {item.recipient_name}</span>
                ) : null}
                <div className="project-milestone-history__meta">
                  {formatProjectMoney(item.amount_raised || 0, item.currency)}
                  {item.goal_amount != null
                    ? ` raised of ${formatProjectMoney(item.goal_amount, item.currency)}`
                    : " raised"}
                  {item.completed_at
                    ? ` · ${String(item.completed_at).slice(0, 10)}`
                    : ""}
                </div>
                {item.impact_summary ? (
                  <p className="project-milestone-history__summary">
                    {item.impact_summary}
                  </p>
                ) : null}
                {item.notes ? (
                  <p className="project-milestone-history__notes">{item.notes}</p>
                ) : null}
                {item.image_path ? (
                  <img
                    className="project-milestone-history__photo"
                    src={resolveMediaUrl(item.image_path)}
                    alt=""
                    onError={handleMediaImgError}
                  />
                ) : null}
                {(item.links || []).length ? (
                  <ul className="project-milestone-history__links">
                    {item.links.map((link, idx) =>
                      link && link.url ? (
                        <li key={`link-${idx}`}>
                          <a
                            href={normalizeExternalUrl(link.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link.label || link.url}
                          </a>
                        </li>
                      ) : null
                    )}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default ProjectMilestones;
